import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { PageHeader, Surface, Empty } from "../components/Primitives";
import { TrendingDown, AlertTriangle, ShoppingCart, Info } from "lucide-react";

const RISK = {
  rupture: { label: "RUPTURE", cls: "badge-alert", color: "var(--alert,#c32a2a)" },
  critique: { label: "CRITIQUE (<7j)", cls: "badge-alert", color: "var(--alert,#c32a2a)" },
  alerte: { label: "ALERTE (<14j)", cls: "badge-warn", color: "#B45309" },
  surveiller: { label: "SURVEILLER (<30j)", cls: "badge-info", color: "#1e60c1" },
  ok: { label: "OK", cls: "badge-success", color: "var(--primary,#C2569B)" },
  stable: { label: "PAS DE VENTE", cls: "badge-warn", color: "#888" },
};

export default function Forecast() {
  const [data, setData] = useState(null);
  const [windowDays, setWindowDays] = useState(30);
  const [filterRisk, setFilterRisk] = useState("all");

  const load = async () => {
    setData(null);
    const { data } = await api.get(`/forecast/stockouts?window_days=${windowDays}`);
    setData(data);
  };
  useEffect(() => { load(); }, [windowDays]); // eslint-disable-line

  const items = useMemo(() => {
    if (!data) return [];
    if (filterRisk === "all") return data.items;
    return data.items.filter((x) => x.risk === filterRisk);
  }, [data, filterRisk]);

  const counts = useMemo(() => {
    const c = { all: data?.items.length || 0 };
    for (const k of Object.keys(RISK)) c[k] = 0;
    for (const it of data?.items || []) c[it.risk] = (c[it.risk] || 0) + 1;
    return c;
  }, [data]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Prédiction ruptures"
        subtitle="Analyse · Moyenne mobile des ventes"
        testId="forecast-header"
        actions={
          <select
            data-testid="window-select"
            className="input-base text-sm"
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
          >
            <option value="7">Fenêtre : 7 jours</option>
            <option value="14">Fenêtre : 14 jours</option>
            <option value="30">Fenêtre : 30 jours</option>
            <option value="60">Fenêtre : 60 jours</option>
            <option value="90">Fenêtre : 90 jours</option>
          </select>
        }
      />

      <div className="surface p-4 flex gap-3 items-start" data-testid="forecast-help">
        <Info size={16} className="text-[var(--primary,#C2569B)] flex-shrink-0 mt-0.5" />
        <div className="text-sm text-[var(--text-muted)] leading-relaxed">
          <span className="font-medium text-[var(--text-base,#0a0a0a)]">Comment ça marche :</span> on calcule la <b>consommation
          quotidienne moyenne</b> sur les {windowDays} derniers jours (à partir des ventes), puis on estime les
          <b> jours restants avant rupture</b> = stock&nbsp;actuel ÷ consommation/jour. Une <b>quantité à recommander</b> est
          également suggérée pour couvrir la prochaine fenêtre de {windowDays}j + le seuil alerte.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {["all", "rupture", "critique", "alerte", "surveiller", "ok", "stable"].map((k) => {
          const active = filterRisk === k;
          const lbl = k === "all" ? "Tous" : RISK[k].label;
          return (
            <button
              key={k}
              data-testid={`f-${k}`}
              type="button"
              onClick={() => setFilterRisk(k)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                active
                  ? "bg-[var(--primary,#C2569B)] text-white border-[var(--primary,#C2569B)]"
                  : "bg-white border-[var(--border-line)] text-[var(--text-muted)] hover:border-[var(--primary,#C2569B)]/50"
              }`}
            >
              {lbl} <span className="opacity-70 ml-1">({counts[k] || 0})</span>
            </button>
          );
        })}
      </div>

      <Surface testId="forecast-table">
        {!data ? (
          <div className="p-6 text-sm text-[var(--text-muted)] font-data">Calcul en cours...</div>
        ) : items.length === 0 ? <Empty message="Aucun produit dans cette catégorie" /> : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Produit</th>
                <th className="text-right">Stock</th>
                <th className="text-right">Vendu ({windowDays}j)</th>
                <th className="text-right">Conso/jour</th>
                <th className="text-right">Jours avant rupture</th>
                <th className="text-right">À commander</th>
                <th>Risque</th>
              </tr>
            </thead>
            <tbody>
              {items.map((x) => {
                const r = RISK[x.risk] || RISK.ok;
                return (
                  <tr key={x.product_id} data-testid={`f-row-${x.sku}`}>
                    <td>
                      <div className="font-medium">{x.name}</div>
                      <div className="font-data text-xs text-[var(--text-muted)]">{x.sku}</div>
                    </td>
                    <td className={`font-data text-right ${x.current_stock <= 0 ? "text-[var(--alert,#c32a2a)] font-bold" : ""}`}>
                      {x.current_stock}
                    </td>
                    <td className="font-data text-right text-[var(--text-muted)]">{x.sold_window}</td>
                    <td className="font-data text-right">{x.daily_avg}</td>
                    <td className={`font-data text-right ${
                      x.days_until_stockout == null ? "text-[var(--text-muted)]" :
                      x.days_until_stockout <= 7 ? "text-[var(--alert,#c32a2a)] font-bold" :
                      x.days_until_stockout <= 14 ? "text-[#B45309] font-semibold" : ""
                    }`}>
                      {x.days_until_stockout == null ? "—" : `${x.days_until_stockout} j`}
                    </td>
                    <td className="font-data text-right">
                      {x.suggested_reorder_qty > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[var(--primary-soft,#F8E8F1)] text-[var(--primary-dark,#8E3A6D)] font-semibold">
                          <ShoppingCart size={11}/> {x.suggested_reorder_qty}
                        </span>
                      ) : "—"}
                    </td>
                    <td><span className={r.cls}>{r.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Surface>

      {data && (
        <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] font-data justify-end">
          <TrendingDown size={12} />
          Généré le {data.generated_at?.slice(0, 19).replace("T", " ")}
        </div>
      )}
    </div>
  );
}
